import {
  createStore,
  createHook,
  Action,
  createContainer,
} from 'react-sweet-state';

import { Project } from 'src/components/types';
import { ProjectOption } from 'src/sections/dashboard/types';
import { stringify } from 'src/urls/utils';
import urls from 'src/urls/workspaces';
import authRequest, { jsonHeaders } from 'src/utils/fetch';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { ProjectFilterState } from './types';

const setCurrentProject =
  (project: ProjectOption | null): Action<ProjectFilterState> =>
  ({ setState }) => {
    setState({ currentProject: project });
  };

const setProjects =
  (projects: Project[]): Action<ProjectFilterState> =>
  ({ setState }) => {
    setState({ projects });
  };

const fetchProjects =
  (workspaceSlug: string, search: string): Action<ProjectFilterState> =>
  async ({ dispatch, setState }) => {
    setState({
      loading: true,
      error: false,
    });

    try {
      const defParams = {
        sort: 'name',
        fields: '-values.owner,-values.workspace',
      };

      const params = stringify(
        search ? { ...defParams, q: `name~"${search}"` } : defParams
      );

      const response = await fetch(
        authRequest(`${urls.api.v20.projects(workspaceSlug)}/${params}`, {
          headers: jsonHeaders,
        })
      );

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const { values }: { values: Project[] } = await response.json();
      dispatch(setProjects(values));
    } catch (e) {
      setState({
        error: true,
      });
      captureExceptionWithTags(e);
    } finally {
      setState({
        loading: false,
      });
    }
  };

const fetchCurrentProject =
  (workspaceSlug: string, uuid: string): Action<ProjectFilterState> =>
  async ({ getState, dispatch }) => {
    try {
      const params = stringify({
        q: `uuid="${uuid}"`,
        fields: '-values.owner,-values.workspace',
      });
      const response = await fetch(
        authRequest(`${urls.api.v20.projects(workspaceSlug)}/${params}`, {
          headers: jsonHeaders,
        })
      );
      if (!response.ok) {
        return;
      }
      const { values }: { values: Project[] } = await response.json();

      // Fetching a project given a uuid should return one value.
      const project = values[0];
      if (getState().currentProject?.value !== project) {
        dispatch(
          setCurrentProject({
            label: project.name,
            value: project,
          })
        );
      }
    } catch (e) {
      captureExceptionWithTags(e);
    }
  };

export const actions = {
  setCurrentProject,
  fetchCurrentProject,
  fetchProjects,
};

const initialState: ProjectFilterState = {
  projects: [],
  currentProject: null,
  loading: false,
  error: false,
};

const WorkspaceProjectFilterStore = createStore({
  initialState,
  actions,
});

export const WorkspaceProjectFilterContainer = createContainer(
  WorkspaceProjectFilterStore
);

export const useWorkspaceProjectFilter = createHook(
  WorkspaceProjectFilterStore
);
